import {type ReactElement, useLayoutEffect, useRef, useState} from 'react';
import {Button} from '@kroll-resolver-crisp/ui-components-react';

// The nominal height of a widget. One that would be taller folds to it, with a Show more button, so
// a long list does not stretch the dashboard around it and nothing scrolls inside the page's scroll.
export const NOMINAL_HEIGHT_PX = 384;

// Opt-in: a block folds only when the page says so, and only when its content is actually taller
// than the nominal height; a short one shows no button at all.
export function ExpandableBody({
  collapsible,
  label,
  children
}: {
  readonly collapsible: boolean | undefined;
  readonly label: string;
  readonly children: ReactElement;
}) {
  const content = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [tall, setTall] = useState(false);

  useLayoutEffect(() => {
    const element = content.current;
    if (!collapsible || element === null) {
      return;
    }

    const measure = () => {
      setTall(element.scrollHeight > NOMINAL_HEIGHT_PX);
    };

    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    // The content changes size when its rows do (a new filter), so keep measuring.
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [collapsible]);

  if (!collapsible) {
    return children;
  }

  const folded = tall && !expanded;
  return (
    <div>
      <div
        data-folded={folded}
        className={folded ? 'overflow-hidden' : undefined}
        style={folded ? {maxHeight: NOMINAL_HEIGHT_PX} : undefined}>
        <div ref={content}>{children}</div>
      </div>
      {tall ? (
        <div className="flex justify-center pt-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Show less' : 'Show more'}: ${label}`}
            onClick={() => {
              setExpanded(!expanded);
            }}>
            {expanded ? 'Show less' : 'Show more'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
